import { useRef, useState, useId } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { FileText, Image, Upload, Trash2, ExternalLink, Loader2, Film, X, Bell } from 'lucide-react'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

export interface DocFile {
  name: string
  url: string
  size: number
  type: string
  uploaded_at: string
  uploaded_by: string
  sharepoint_path: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(iso: string): string {
  return formatMyanmarDateTime(iso) || iso
}

function DocFileIcon({ type }: { type: string }) {
  if (type.startsWith('image/')) return <Image size={14} className="text-purple-500 shrink-0" />
  if (type.startsWith('video/')) return <Film size={14} className="text-blue-500 shrink-0" />
  return <FileText size={14} className="text-red-700 shrink-0" />
}

interface DocCategoryProps {
  title: string
  description: string
  files: DocFile[]
  canUpload: boolean
  canDelete: boolean
  uploadUrl: string
  deleteUrl: string
  accept?: string
  invalidateKeys: string[][]
  onMutated?: () => void
  color?: 'blue' | 'amber' | 'green'
  notifyUrl?: string
  notifyDocType?: string
}

function DocCategory({
  title, description, files, canUpload, canDelete,
  uploadUrl, deleteUrl, accept, invalidateKeys, onMutated, color = 'blue',
  notifyUrl, notifyDocType,
}: DocCategoryProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileInputId = useId()
  const [uploadError, setUploadError] = useState('')
  const [deletingPath, setDeletingPath] = useState<string | null>(null)
  const [pendingNotify, setPendingNotify] = useState<{name: string, download_url: string, web_url: string}[]>([])
  const [isNotifying, setIsNotifying] = useState(false)

  const colorMap = {
    blue: { header: 'bg-blue-50 border-blue-200', title: 'text-blue-900', sub: 'text-blue-700', btn: 'bg-blue-700 hover:bg-blue-800' },
    amber: { header: 'bg-amber-50 border-amber-200', title: 'text-amber-900', sub: 'text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700' },
    green: { header: 'bg-green-50 border-green-200', title: 'text-green-900', sub: 'text-green-700', btn: 'bg-green-700 hover:bg-green-800' },
  }
  const c = colorMap[color]

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.post(uploadUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => {
      setUploadError('')
      invalidateKeys.forEach(k => queryClient.invalidateQueries({ queryKey: k }))
      onMutated?.()
    },
    onError: (err: any) => {
      setUploadError(err.response?.data?.detail || 'Upload failed')
    },
  })

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList) return
    setUploadError('')
    const newPending: {name: string, download_url: string, web_url: string}[] = []
    for (let i = 0; i < fileList.length; i++) {
      try {
        const res = await upload.mutateAsync(fileList[i])
        if (notifyUrl && (res.data?.file?.download_url || res.data?.file?.url)) {
          newPending.push({
            name: res.data.file.name || fileList[i].name,
            download_url: res.data.file.download_url || '',
            web_url: res.data.file.url || '',
          })
        }
      } catch {}
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (newPending.length > 0) {
      setPendingNotify(prev => [...prev, ...newPending])
    }
  }

  const handleNotify = async () => {
    if (!notifyUrl || pendingNotify.length === 0) return
    setIsNotifying(true)
    try {
      const res = await api.post(notifyUrl, {
        doc_type: notifyDocType || 'site-photos',
        download_urls: pendingNotify.map(p => p.download_url).filter(Boolean),
        names: pendingNotify.map(p => p.name),
        web_urls: pendingNotify.map(p => p.web_url).filter(Boolean),
      })
      if (res.data?.notification_sent !== false) {
        setPendingNotify([])
      } else {
        setUploadError('Notification could not be delivered. Please try again.')
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Failed to send notification')
    } finally {
      setIsNotifying(false)
    }
  }

  const handleDelete = async (f: DocFile) => {
    if (!confirm(`Delete "${f.name}"?`)) return
    setDeletingPath(f.sharepoint_path)
    try {
      await api.delete(deleteUrl, { params: { sp_path: f.sharepoint_path } })
      invalidateKeys.forEach(k => queryClient.invalidateQueries({ queryKey: k }))
      onMutated?.()
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Delete failed')
    } finally {
      setDeletingPath(null)
    }
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className={`px-4 py-2.5 border-b ${c.header} flex items-center justify-between gap-2 flex-wrap`}>
        <div>
          <span className={`text-sm font-semibold ${c.title}`}>{title}</span>
          <span className="text-xs text-slate-500 ml-2">({files.length} file{files.length !== 1 ? 's' : ''})</span>
        </div>
        <div className="flex items-center gap-2">
          {canUpload && (
            <label
              htmlFor={fileInputId}
              aria-disabled={upload.isPending}
              className={`flex items-center gap-1.5 text-xs text-white px-2.5 py-1 rounded-lg cursor-pointer select-none ${upload.isPending ? 'opacity-50 pointer-events-none' : ''} ${c.btn}`}
            >
              {upload.isPending ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
              Upload
              <input
                id={fileInputId}
                ref={fileInputRef}
                type="file"
                multiple
                accept={accept}
                className="hidden"
                disabled={upload.isPending}
                onChange={e => handleFiles(e.target.files)}
              />
            </label>
          )}
          {notifyUrl && pendingNotify.length > 0 && (
            <button
              type="button"
              onClick={handleNotify}
              disabled={isNotifying}
              className="flex items-center gap-1.5 text-xs text-white px-2.5 py-1 rounded-lg bg-green-700 hover:bg-green-800 disabled:opacity-50"
            >
              {isNotifying ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
              Notify Team ({pendingNotify.length})
            </button>
          )}
        </div>
      </div>

      <div className="px-3 py-1 bg-slate-50 border-b border-slate-100">
        <p className="text-[10px] text-slate-400">{description}</p>
      </div>

      {uploadError && (
        <div className="flex items-center gap-2 mx-3 mt-2 text-xs text-red-700 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
          <span className="flex-1">{uploadError}</span>
          <button onClick={() => setUploadError('')}><X size={11} /></button>
        </div>
      )}

      {files.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-6">
          {canUpload ? 'No files yet — click Upload to add one' : 'No files uploaded yet'}
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 transition-colors">
              <DocFileIcon type={f.type} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 truncate font-medium">{f.name}</p>
                <p className="text-[10px] text-slate-400">
                  {f.uploaded_by && <span>{f.uploaded_by} · </span>}
                  {f.uploaded_at && <span>{formatDate(f.uploaded_at)}</span>}
                  {f.size > 0 && <span> · {formatFileSize(f.size)}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {f.sharepoint_path && (
                  <button
                    type="button"
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      const base = `/api/projects/sharepoint-download?path=${encodeURIComponent(f.sharepoint_path)}`;
                      window.open(token ? `${base}&token=${encodeURIComponent(token)}` : base, '_blank', 'noopener,noreferrer');
                    }}
                    className="p-1 text-slate-400 hover:text-red-700 rounded"
                    title="Open file"
                  >
                    <ExternalLink size={13} />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(f)}
                    disabled={deletingPath === f.sharepoint_path}
                    className="p-1 text-slate-300 hover:text-red-500 rounded disabled:opacity-40"
                    title="Delete file"
                  >
                    {deletingPath === f.sharepoint_path
                      ? <Loader2 size={13} className="animate-spin" />
                      : <Trash2 size={13} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export interface ProjectDocumentPanelProps {
  projectId: number
  projectCode: string
  projectType: string
  assignedTo: string
  status: string
  dateCompleted?: string | null
  userRole: string
  userName: string
  sitePhotos: DocFile[]
  preInstallDocs: DocFile[]
  completionDocs: DocFile[]
  context: 'pm' | 'project-bank'
  invalidateQueryKeys: string[][]
  /** Limit which categories are rendered; omit to show all allowed */
  visibleCategories?: ('site-photos' | 'pre-install' | 'completion')[]
  /** Called after any successful upload or delete */
  onMutated?: () => void
  /** @deprecated unused, kept for prop-drilling compat */
  onFileChanged?: (updated: any) => void
  /** @deprecated unused, kept for prop-drilling compat */
  projectId_forSync?: number
}

const PRE_INSTALL_UPLOAD_ROLES = [
  'super_admin', 'admin',
  'sales_manager', 'asst_sales_manager', 'sales_executive',
  'procurement', 'storekeeper', 'finance',
]
const ADMIN_ROLES = ['super_admin', 'admin']
const COMPLETION_ALWAYS_VISIBLE_ROLES = [
  'super_admin', 'admin',
  'sales_manager', 'asst_sales_manager', 'sales_executive', 'finance',
]
const ENGINEERING_ROLES = ['chief_engineer', 'operation_manager', 'engineer', 'technician']
const PM_WINDOW_DAYS = 14

function parseAssignedToTL(assignedTo: string): string {
  if (!assignedTo) return ''
  try {
    const p = JSON.parse(assignedTo)
    if (p && typeof p === 'object') return p.team_leader || ''
  } catch {}
  return assignedTo
}

/** Returns true if project is still within the PM visibility window */
function isWithinPMWindow(status: string, dateCompleted?: string | null): boolean {
  if (status !== 'Complete') return true
  if (!dateCompleted) return false
  const completed = new Date(dateCompleted)
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - PM_WINDOW_DAYS)
  return completed >= cutoff
}

export default function ProjectDocumentPanel({
  projectId, projectCode, projectType, assignedTo, status, dateCompleted,
  userRole, userName, sitePhotos, preInstallDocs, completionDocs,
  context, invalidateQueryKeys, visibleCategories, onMutated,
}: ProjectDocumentPanelProps) {
  const teamLeader = parseAssignedToTL(assignedTo)
  const isTL = !!teamLeader && userName === teamLeader
  const isEngineeringRole = ENGINEERING_ROLES.includes(userRole)

  const canUploadSitePhotos = true
  const canUploadPreInstall = PRE_INSTALL_UPLOAD_ROLES.includes(userRole) || isTL
  const canUploadCompletion = ADMIN_ROLES.includes(userRole) || isTL

  const canDeleteSitePhotos = canUploadSitePhotos
  const canDeletePreInstall = canUploadPreInstall
  const canDeleteCompletion = canUploadCompletion

  const canSeePreInstall = userRole !== 'technician'

  let canSeeCompletion: boolean
  if (context === 'pm') {
    canSeeCompletion = true
  } else {
    if (COMPLETION_ALWAYS_VISIBLE_ROLES.includes(userRole)) {
      canSeeCompletion = true
    } else if (userRole === 'technician') {
      canSeeCompletion = false
    } else if (isEngineeringRole) {
      // TL can see completion docs while project is within the 14-day PM window
      canSeeCompletion = isTL && isWithinPMWindow(status, dateCompleted)
    } else {
      canSeeCompletion = false
    }
  }

  const baseUrl = `/project-management/${projectId}`
  const show = (cat: 'site-photos' | 'pre-install' | 'completion') =>
    !visibleCategories || visibleCategories.includes(cat)

  return (
    <div className="space-y-4">
      {show('site-photos') && (
        <DocCategory
          title="Site Photos"
          description="Site visit photos and general project images — visible to everyone"
          files={sitePhotos}
          canUpload={canUploadSitePhotos}
          canDelete={canDeleteSitePhotos}
          uploadUrl={`${baseUrl}/site-photos`}
          deleteUrl={`${baseUrl}/site-photos`}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          invalidateKeys={invalidateQueryKeys}
          onMutated={onMutated}
          color="blue"
          notifyUrl={`${baseUrl}/batch-notify`}
          notifyDocType="site-photos"
        />
      )}

      {show('pre-install') && canSeePreInstall && (
        <DocCategory
          title="Pre-Installation Documents"
          description="BOQ, issue lists, Excel, PDF — visible to all except technician"
          files={preInstallDocs}
          canUpload={canUploadPreInstall}
          canDelete={canDeletePreInstall}
          uploadUrl={`${baseUrl}/pre-install-docs`}
          deleteUrl={`${baseUrl}/pre-install-docs`}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          invalidateKeys={invalidateQueryKeys}
          onMutated={onMutated}
          color="amber"
          notifyUrl={`${baseUrl}/batch-notify`}
          notifyDocType="pre-install-docs"
        />
      )}

      {show('completion') && canSeeCompletion && (
        <DocCategory
          title="Project Complete Data"
          description="Handover forms, completion photos, videos — TL uploads only"
          files={completionDocs}
          canUpload={canUploadCompletion}
          canDelete={canDeleteCompletion}
          uploadUrl={`${baseUrl}/completion-docs`}
          deleteUrl={`${baseUrl}/completion-docs`}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov,.zip"
          invalidateKeys={invalidateQueryKeys}
          onMutated={onMutated}
          color="green"
          notifyUrl={`${baseUrl}/batch-notify`}
          notifyDocType="completion-docs"
        />
      )}
    </div>
  )
}
